import {engineContext} from './context.js';

/* ZooInvest Contradiction Resolution Engine v0.1.0-alpha
 * Engine-only. Resolves duplicate/conflicting warnings from multiple engines.
 * Goal: one clean reasoned challenge, not warning spam.
 * No universal score. No BUY/SELL. No Company Quality rewrite.
 * Thresholds PROVISIONAL / UNCALIBRATED.
 */
(function(root,factory){
 const api=factory();
 if(root) root.ZooInvestContradictionResolutionEngine=api;
})(engineContext,function(){
 'use strict';
 const VERSION='0.1.0-alpha';
 const CONFIG=Object.freeze({
   calibrated:false,
   thresholds:'PROVISIONAL / UNCALIBRATED',
   maxPrimary:4,
   maxSecondary:5
 });
 const norm=v=>String(v??'').trim().toUpperCase();
 const freeze=o=>Object.freeze(o);

 function normalizeSignal(s={}){
   const verification=['VERIFIED','PARTIAL','UNVERIFIED'].includes(norm(s.verification))?norm(s.verification):'UNVERIFIED';
   const severity=['CRITICAL','HIGH','MEDIUM','LOW','INFO'].includes(norm(s.severity))?norm(s.severity):'MEDIUM';
   return freeze({
     id:String(s.id||s.code||'UNKNOWN'),
     code:norm(s.code||s.id||'UNKNOWN'),
     domain:norm(s.domain||'GENERAL'),
     claim:String(s.claim||s.message||''),
     direction:norm(s.direction||'RISK'),
     severity,
     verification,
     independent:s.independent===true,
     sourceCount:Number.isInteger(s.sourceCount)?Math.max(0,s.sourceCount):0,
     evidenceIds:Array.isArray(s.evidenceIds)?freeze([...new Set(s.evidenceIds.map(String))]):freeze([]),
     personalAlert:s.personalAlert===true,
     owned:s.owned===true,
     timestamp:s.timestamp?String(s.timestamp):null
   });
 }

 function severityRank(s){return {CRITICAL:5,HIGH:4,MEDIUM:3,LOW:2,INFO:1}[s]||0}
 function verificationRank(v){return {VERIFIED:3,PARTIAL:2,UNVERIFIED:1}[v]||0}

 function evidenceOverlap(a,b){
   if(!a.evidenceIds.length||!b.evidenceIds.length) return 0;
   const A=new Set(a.evidenceIds);
   const common=b.evidenceIds.filter(x=>A.has(x)).length;
   return common/Math.max(a.evidenceIds.length,b.evidenceIds.length);
 }

 function dedupe(signals=[]){
   const rows=signals.map(normalizeSignal);
   const groups=[];
   for(const s of rows){
     let g=groups.find(x=>x.code===s.code || (
       x.domain===s.domain &&
       x.direction===s.direction &&
       evidenceOverlap(x.representative,s)>=0.6
     ));
     if(!g){
       groups.push({
         code:s.code,domain:s.domain,direction:s.direction,
         representative:s,signals:[s]
       });
     } else g.signals.push(s);
   }
   return freeze(groups.map(g=>{
     const sorted=[...g.signals].sort((a,b)=>
       severityRank(b.severity)-severityRank(a.severity) ||
       verificationRank(b.verification)-verificationRank(a.verification) ||
       b.sourceCount-a.sourceCount
     );
     const rep=sorted[0];
     const verifiedCount=sorted.filter(x=>x.verification==='VERIFIED').length;
     const independentCount=sorted.filter(x=>x.independent).length;
     return freeze({
       code:g.code,domain:g.domain,direction:g.direction,
       representative:rep,
       duplicates:sorted.length-1,
       verifiedCount,
       independentCount,
       corroborated:verifiedCount>=2 && independentCount>=2,
       evidenceIds:freeze([...new Set(sorted.flatMap(x=>x.evidenceIds))]),
       sourceSignals:freeze(sorted)
     });
   }));
 }

 function contradictionPairs(groups=[]){
   const pairs=[];
   for(let i=0;i<groups.length;i++){
     for(let j=i+1;j<groups.length;j++){
       const a=groups[i],b=groups[j];
       if(a.domain!==b.domain) continue;
       if(a.direction===b.direction) continue;
       const overlap=evidenceOverlap(a.representative,b.representative);
       if(overlap>0 || a.code===b.code){
         pairs.push(freeze({
           domain:a.domain,
           a:a.code,b:b.code,
           directionA:a.direction,directionB:b.direction,
           evidenceOverlap:Number(overlap.toFixed(2)),
           state:'UNRESOLVED'
         }));
       }
     }
   }
   return freeze(pairs);
 }

 function priority(group){
   const s=group.representative;
   let p=severityRank(s.severity)*20 + verificationRank(s.verification)*7;
   if(group.corroborated) p+=12;
   else if(group.independentCount>=1) p+=4;
   if(s.personalAlert && !s.owned) p-=20; // research signal must not masquerade as owned warning
   if(s.verification==='UNVERIFIED') p-=12;
   return p;
 }

 function resolve(signals=[]){
   const groups=dedupe(signals);
   const contradictions=contradictionPairs(groups);
   const ranked=[...groups].sort((a,b)=>priority(b)-priority(a));
   const primary=ranked.slice(0,CONFIG.maxPrimary);
   const secondary=ranked.slice(CONFIG.maxPrimary,CONFIG.maxPrimary+CONFIG.maxSecondary);

   const criticalUnresolved=contradictions.some(c=>{
     const ga=groups.find(g=>g.code===c.a),gb=groups.find(g=>g.code===c.b);
     return [ga,gb].some(g=>g&&severityRank(g.representative.severity)>=4);
   });

   const state=criticalUnresolved?'BLOCKED':primary.some(g=>severityRank(g.representative.severity)>=4)?'REVIEW':'OPEN';

   return freeze({
     state,
     primary:freeze(primary),
     secondary:freeze(secondary),
     contradictions,
     hiddenDuplicateCount:groups.reduce((n,g)=>n+g.duplicates,0),
     action:criticalUnresolved?'INVESTIGATE':primary.length?'REVIEW':'NO_ACTION',
     companyQualityEffect:0,
     universalInvestmentScore:null,
     buySignal:false,
     sellSignal:false,
     personalAlertRule:'OWNED_ONLY',
     note:'Correlated warnings are compressed. Conflicting evidence is surfaced instead of silently averaged.'
   });
 }

 function uiSummary(signals=[]){
   const r=resolve(signals);
   return freeze({
     state:r.state,
     primary:freeze(r.primary.map(g=>freeze({
       code:g.code,
       domain:g.domain,
       claim:g.representative.claim,
       severity:g.representative.severity,
       verification:g.representative.verification,
       corroborated:g.corroborated,
       duplicateCount:g.duplicates
     }))),
     contradictionCount:r.contradictions.length,
     hiddenDuplicateCount:r.hiddenDuplicateCount,
     more:r.secondary.length,
     destination:'WHY_WRONG / ATTENTION_QUEUE'
   });
 }

 function selfTest(){
   const tests=[],t=(n,p)=>tests.push({name:n,pass:!!p});
   const signals=[
     {code:'NOT_PROFITABLE',domain:'FUNDAMENTALS',direction:'RISK',severity:'HIGH',verification:'VERIFIED',independent:true,sourceCount:1,evidenceIds:['e1'],claim:'Company is not profitable'},
     {code:'NOT_PROFITABLE',domain:'FUNDAMENTALS',direction:'RISK',severity:'HIGH',verification:'VERIFIED',independent:true,sourceCount:1,evidenceIds:['e2'],claim:'Negative net income'},
     {code:'INDUSTRY_WEAK',domain:'SECTOR',direction:'RISK',severity:'HIGH',verification:'VERIFIED',independent:true,evidenceIds:['s1'],claim:'Sector demand weak'},
     {code:'AUTHORITY_BIAS',domain:'DECISION',direction:'RISK',severity:'MEDIUM',verification:'PARTIAL',independent:false,claim:'Thesis relies on expert opinion'},
     {code:'SECTOR_RECOVERY',domain:'SECTOR',direction:'SUPPORT',severity:'HIGH',verification:'VERIFIED',independent:true,evidenceIds:['s1'],claim:'Sector recovering'}
   ];
   let r=resolve(signals);
   t('Duplicate warnings are compressed',r.hiddenDuplicateCount===1);
   t('Independent verified duplicates become corroborated',r.primary.some(g=>g.code==='NOT_PROFITABLE'&&g.corroborated));
   t('Conflicting sector evidence is surfaced',r.contradictions.length===1);
   t('Critical unresolved contradiction blocks compressed conclusion',r.state==='BLOCKED');
   t('No universal score is created',r.universalInvestmentScore===null);
   t('No buy/sell signal is created',!r.buySignal&&!r.sellSignal);
   t('Company Quality is untouched',r.companyQualityEffect===0);
   t('Primary list is capped',r.primary.length<=CONFIG.maxPrimary);
   r=resolve([
     {code:'WATCH_SIGNAL',domain:'EVENT',direction:'RISK',severity:'HIGH',verification:'VERIFIED',personalAlert:true,owned:false,claim:'Watchlist event'}
   ]);
   t('Unowned signal does not gain personal-warning priority',r.personalAlertRule==='OWNED_ONLY');
   const u=uiSummary(signals);
   t('UI summary exposes hidden duplicate count',u.hiddenDuplicateCount===1);
   t('UI summary routes to WHY WRONG / ATTENTION QUEUE',u.destination==='WHY_WRONG / ATTENTION_QUEUE');
   r=resolve([{code:'A',domain:'X',direction:'RISK',severity:'LOW',verification:'UNVERIFIED',claim:'weak clue'}]);
   t('Single weak unverified clue does not manufacture blocker',r.state!=='BLOCKED');
   return freeze({passed:tests.filter(x=>x.pass).length,total:tests.length,tests});
 }

 const API={VERSION,CONFIG,normalizeSignal,severityRank,verificationRank,evidenceOverlap,dedupe,contradictionPairs,priority,resolve,uiSummary,selfTest};
 try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
 return freeze(API);
});
